
import { useQuery } from '@tanstack/react-query';
import { fetchBotSignals, fetchCTraderSignalStatuses } from '@/services/signal-service';
import { Signal, SignalAccountStatus } from '@/types/signal';
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';

export const usePremiumBotSignals = (botId: string | undefined) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<string>("bot-signals");
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Pagination state
  const [botSignalsPage, setBotSignalsPage] = useState<number>(1);
  const [botSignalsPageSize, setBotSignalsPageSize] = useState<number>(10);
  const [ctraderSignalsPage, setCtraderSignalsPage] = useState<number>(1);
  const [ctraderSignalsPageSize, setCtraderSignalsPageSize] = useState<number>(10);
  
  // Fetch regular bot signals with pagination
  const {
    data: botSignalsResponse,
    isLoading: isBotSignalsLoading,
    error: botSignalsError,
    refetch: refetchBotSignals
  } = useQuery({
    queryKey: ['premiumBotSignals', botId, botSignalsPage, botSignalsPageSize],
    queryFn: async () => {
      if (!botId) return { data: [], total: 0, page: botSignalsPage, pageSize: botSignalsPageSize };
      return fetchBotSignals(botId, botSignalsPage, botSignalsPageSize);
    },
    enabled: !!botId && !!user?.id
  });
  
  // Fetch CTrader signal statuses with pagination
  const {
    data: ctraderSignalsResponse,
    isLoading: isCtraderSignalsLoading,
    error: ctraderSignalsError,
    refetch: refetchCtraderSignals
  } = useQuery({
    queryKey: ['premiumCtraderSignals', botId, ctraderSignalsPage, ctraderSignalsPageSize],
    queryFn: async () => {
      if (!botId) return { data: [], total: 0, page: ctraderSignalsPage, pageSize: ctraderSignalsPageSize };
      return fetchCTraderSignalStatuses(botId, ctraderSignalsPage, ctraderSignalsPageSize);
    },
    enabled: !!botId && !!user?.id && activeTab === "ctrader-signals"
  });
  
  // Extract values with defaults
  const botSignals = botSignalsResponse?.data || [];
  const botSignalsTotal = botSignalsResponse?.total || 0;
  const ctraderSignals = ctraderSignalsResponse?.data || [];
  const ctraderSignalsTotal = ctraderSignalsResponse?.total || 0;
  
  // Filter bot signals based on search query and status filter
  const filteredBotSignals = botSignals.filter(signal => {
    // Apply search filter
    const matchesSearch = searchQuery
      ? signal.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        signal.instrument.toLowerCase().includes(searchQuery.toLowerCase()) ||
        signal.action.toLowerCase().includes(searchQuery.toLowerCase())
      : true;
    
    // Apply status filter
    const matchesStatus = statusFilter === 'all' 
      ? true
      : signal.status.toLowerCase() === statusFilter.toLowerCase();
    
    return matchesSearch && matchesStatus;
  });
  
  // Filter ctrader signals based on search query and status filter
  const filteredCtraderSignals = ctraderSignals.filter(signal => {
    // Apply search filter
    const matchesSearch = searchQuery
      ? signal.signal_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        signal.symbol?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        signal.action?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        signal.number_account_trading?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        signal.csp_account_name?.toLowerCase().includes(searchQuery.toLowerCase())
      : true;
    
    // Apply status filter
    const matchesStatus = statusFilter === 'all' 
      ? true
      : signal.status.toLowerCase() === statusFilter.toLowerCase();
    
    return matchesSearch && matchesStatus;
  });
  
  // Pagination handlers
  const handleBotSignalsPageChange = (page: number) => {
    setBotSignalsPage(page);
  };
  
  const handleBotSignalsPageSizeChange = (pageSize: number) => {
    setBotSignalsPageSize(pageSize);
    setBotSignalsPage(1); // Reset to first page when changing page size
  };
  
  const handleCtraderSignalsPageChange = (page: number) => {
    setCtraderSignalsPage(page);
  };
  
  const handleCtraderSignalsPageSizeChange = (pageSize: number) => {
    setCtraderSignalsPageSize(pageSize);
    setCtraderSignalsPage(1); // Reset to first page when changing page size
  };
  
  // Function to refresh signals
  const refreshSignals = () => {
    refetchBotSignals();
    if (activeTab === "ctrader-signals") {
      refetchCtraderSignals();
    }
  };
  
  return {
    botSignals: filteredBotSignals,
    ctraderSignals: filteredCtraderSignals,
    botSignalsTotal,
    ctraderSignalsTotal,
    botSignalsPage,
    botSignalsPageSize,
    ctraderSignalsPage,
    ctraderSignalsPageSize,
    isBotSignalsLoading,
    isCtraderSignalsLoading,
    botSignalsError,
    ctraderSignalsError,
    activeTab,
    setActiveTab,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    handleBotSignalsPageChange,
    handleBotSignalsPageSizeChange,
    handleCtraderSignalsPageChange,
    handleCtraderSignalsPageSizeChange,
    refreshSignals
  };
};
